import { useRef } from "react"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useParams } from "react-router-dom"
import { isWeightable } from "@/lib/utils"

const OrderDetails = () => {
  const { id } = useParams()
  const printRef = useRef(null)

  const {
    data: response,
    isLoading,
    isError,
  } = useCustomQuery(["invoice-detail", id], `order/${id}`)

  if (isLoading) {
    return <div>Loading...</div>
  }

  const order = response?.data
  if (isError || !order) {
    return <div>Unable to load this invoice.</div>
  }

  const branch = order.branch ?? order.store

  const invoiceMessage =
    order.invoice_message || "THANK YOU FOR SHOPPING WITH US!"

  const handlePrint = () => {
    if (!printRef.current) return

    const printContents = printRef.current.innerHTML
    const originalContents = document.body.innerHTML

    // Replace the body content with printable content
    document.body.innerHTML = printContents
    window.print()

    // Restore original body content after print
    document.body.innerHTML = originalContents
  }

  // Calculate VAT amount (reverse from total, excluding delivery fee — VAT applies to goods only)
  const deliveryFee = Number(order.delivery_fee) || 0
  const goodsTotal = order.total_amount - deliveryFee
  const subtotalBeforeVat = goodsTotal / 1.13
  const vatAmount = goodsTotal - subtotalBeforeVat

  // Calculate total quantity sold
  const totalQuantitySold = order.order_items.reduce(
    (acc, item) => acc + item.quantity,
    0
  )

  return (
    <>
      <div
        ref={printRef}
        className="bill-container p-4 font-mono text-sm text-black bg-white max-w-sm mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="font-bold text-2xl">
            {branch?.brand_name || branch?.name || "Branch"}
          </h2>
          {branch?.phone && <p>{branch.phone}</p>}
          {branch?.location && <p>{branch.location}</p>}
          {branch?.vat_pan_number && <p>PAN: {branch.vat_pan_number}</p>}
        </div>

        {/* Transaction Details */}
        <div>
          <p>Invoice Number: {order.invoice_no || order.code}</p>
          <p>Invoice To: {order.customer?.name ?? "Walk-in Customer"}</p>
          <p>Mobile: {order.customer?.phone ?? "-"}</p>
          {order.is_split_payment &&
          order.split_payments &&
          order.split_payments.length > 0 ? (
            <div>
              <p className=" font-bold">Payment Methods:</p>
              {order.split_payments.map((payment, index) => (
                <p key={index}>
                  {payment.display_name || payment.payment_method_name}: Rs.{" "}
                  {payment.amount?.toFixed(2)}
                </p>
              ))}
            </div>
          ) : (
            <p>Method: {order.payment_method}</p>
          )}
          <p>Date: {new Date(order.order_date).toLocaleDateString()} </p>
          <p>Time: {new Date(order.order_date).toLocaleTimeString()}</p>
        </div>

        {/* Product List */}
        <div className="border-t border-b py-2 mb-4">
          <table className="w-full">
            <tbody>
              <tr>
                <td className="text-left">Item</td>
                <td className="text-center">Qty</td>
                {isWeightable() && <td className="text-center">Weight</td>}
                <td className="text-center">Price</td>
                <td className="text-center">Dis</td>
                <td className="text-center">Total</td>
              </tr>
              {order.order_items.map((item, index) => {
                // Price before any discount; Total is what was actually charged
                const listPrice = item.original_price ?? item.sell_price
                const lineTotal = Number(item.total)
                const discountAmount = item.quantity
                  ? listPrice - lineTotal / item.quantity
                  : 0
                return (
                  <tr key={index}>
                    <td>{item.sku}</td>
                    <td className="text-center ">{item.quantity}</td>
                    {isWeightable() && (
                      <td className="text-center ">{item.weight} </td>
                    )}
                    <td className="text-center ">{listPrice?.toFixed(2)}</td>
                    <td className="text-center ">
                      {discountAmount?.toFixed(2)}
                    </td>
                    <td className="text-center ">Rs. {lineTotal.toFixed(2)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Total Discount */}
        <div className="text-right">
          <p className="font-bold ">
            Discount %: {order.discount_percentage?.toFixed(2)}
          </p>
        </div>

        {/* VAT Section */}
        {/* <div className="text-right">
          <p className="font-bold ">
            VAT (13%): Rs. {vatAmount?.toFixed(2)}
          </p>
        </div> */}

        {/* Total Section */}
        <div className="text-right">
          <p className="font-bold ">
            TOTAL: Rs.{" "}
            {Number(order.total_amount) +
              Number(order.final_discount) -
              deliveryFee}
          </p>
        </div>

        <div className="text-right">
          <p className="font-bold ">
            DISCOUNT After VAT: Rs. {Number(order.final_discount)}
          </p>
        </div>

        {deliveryFee > 0 && (
          <div className="text-right">
            <p className="font-bold ">
              DELIVERY FEE: Rs. {deliveryFee.toFixed(2)}
            </p>
          </div>
        )}

        <div className="text-right">
          <p className="font-bold ">
            GRAND TOTAL: Rs. {order.total_amount?.toFixed(2)}
          </p>
        </div>

        {order.payment_method == "Cash" && (
          <div className="text-right mb-4">
            <div className="text-right">
              <p className="font-bold ">
                RECEIVED: Rs. {order.extras?.cash_received?.toFixed(2)}
              </p>
            </div>
            <p className="font-bold ">
              CHANGE: Rs.{" "}
              {(order.extras?.cash_received - order.total_amount)?.toFixed(2)}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="border-b text-center mt-4">
          <p className="pb-2">
            # ITEMS SOLD: {order.order_items.length} TOTAL QTY SOLD:{" "}
            {totalQuantitySold}
          </p>
          <p className="font-semiBold">
            Cashier: {order.createdBy?.name ?? "-"}
          </p>
        </div>

        {/* Thank You Message */}
        <div className="border-b text-center mt-4">
          <p className="font-semiBold">
            ***
            {invoiceMessage}
            ***
          </p>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-center mt-6 print:hidde">
        <button
          onClick={handlePrint}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md"
        >
          Print Receipt
        </button>
      </div>
    </>
  )
}

export default OrderDetails
